/**
 * Values shared by the two quota surfaces (A7Y-3802): the account table on
 * `/admin/automation` and the per-account detail page at
 * `/admin/automation/quota/:account`. Kept apart from the components in
 * `quota-account-parts.tsx` so that file exports components only.
 *
 * It exists so the overview row and the detail page cannot drift into
 * describing the same account differently — the split the ticket asked for is
 * how much of an account is shown, never what any of it means. Every value
 * still comes from the API payload; nothing here invents a role, a boundary or
 * a reading.
 */
import type {
  AiQuotaAccountKey,
  AiQuotaAccountRole,
  AiQuotaAccountStatus,
  AiQuotaProvider,
} from '@a7y/types';

/** Where a given account's detail page lives. One definition, so the row link
 *  and the route registration cannot disagree about the shape. */
export function quotaAccountDetailPath(account: AiQuotaAccountKey | string): string {
  return `/admin/automation/quota/${account}`;
}

/**
 * Operator-facing wording for each role. The role VALUE is the API's, derived
 * from `accountRole()` in the single admission policy — only the label and the
 * explanation are presentation, which is the same split `SessionQuotaUsage`'s
 * decision meta uses.
 *
 * These three are not interchangeable, and rendering the fleet as five
 * undifferentiated "coding accounts" was the defect: an operator reading this
 * panel could not tell that `team@` never builds and `codex` never implements.
 */
export const ROLE_META: Record<AiQuotaAccountRole, { label: string; title: string; badge: string }> =
  {
    orchestrator: {
      label: 'Orchestrator',
      title:
        'Pinned to this account (2026-08-26). Runs selection, planning, Karen review, verification, landing and reporting. Never hosts a worker build — it is removed from the worker driver pool, so it is not selectable for implementation.',
      badge:
        'bg-violet-50 text-violet-700 ring-violet-600/20 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-400/20',
    },
    implementation: {
      label: 'Implementation',
      title:
        'May host worker implementation. Workers build on their own driver account, so this account both drives and builds.',
      badge:
        'bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-400/20',
    },
    'review-only': {
      label: 'Review only',
      title:
        'Review-only (A7Y-3054). Still probed, still ranked, and still the preferred reviewer whenever Claude implemented — but it may not build.',
      badge:
        'bg-sky-50 text-sky-700 ring-sky-600/20 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-400/20',
    },
  };

/**
 * How each service presents in the table. The icon is load-bearing rather than
 * decorative: `tpf@` appears under both providers, so it is what tells two rows
 * with the same mailbox apart — which is why every entry carries a label that
 * becomes the glyph's accessible name and title.
 */
export const PROVIDER_META: Record<AiQuotaProvider, { label: string; className: string }> = {
  claude: { label: 'Claude', className: 'text-orange-600 dark:text-orange-400' },
  codex: { label: 'Codex', className: 'text-zinc-700 dark:text-zinc-300' },
};

export function sourceLabel(account: AiQuotaAccountStatus): string | null {
  if (!account.source) return null;
  return account.source.kind === 'manual' ? 'Manual refresh' : account.source.kind.toUpperCase();
}

/** True when the account has reported at least one successful reading. */
export function hasCachedValues(account: AiQuotaAccountStatus): boolean {
  return account.lastSuccessfulAt != null;
}

/**
 * Why an account has no capacity block. Each cause reads differently to an
 * operator, and none of them is "there is no capacity" — an unreadable lease
 * must never be rendered as an account with nothing reserved.
 */
export function capacityUnavailableReason(
  isError: boolean,
  isLoading: boolean,
  hasData: boolean
): string | null {
  if (isError) return 'Lease capacity could not be read, so ceilings and reservations are unknown.';
  if (isLoading) return 'Loading lease capacity…';
  // The endpoint returns one entry per policy account, so this means the account
  // is outside the policy's own set — which says nothing about what it reserves.
  if (hasData) return 'The lease endpoint returned no record for this account.';
  return null;
}
